'use client';

import { Component } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Per-block error boundary. A render crash in one block stays scoped to
 * its own slot — the rest of the page renders unaffected. Direct port
 * of ThemeMaker's {@code components/block-system/BlockErrorBoundary.tsx}.
 *
 * <p>Class component because {@code componentDidCatch} is the only
 * React error-boundary primitive and it's class-only as of React 19.
 * The functional alternative is a dep (react-error-boundary); a 50-line
 * class earns its keep.
 */
interface Props {
  children: ReactNode;
  blockType: string;
  fallback?: (error: Error, blockType: string) => ReactNode;
}
interface State {
  error: Error | null;
}

export class BlockErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Console for dev. In production this is the Sentry hook point.
    console.error(
      `[site-builder] block "${this.props.blockType}" crashed: ${error.message}\n` +
        `Stack: ${error.stack ?? '(none)'}\n` +
        `Component stack: ${info.componentStack ?? '(none)'}`,
    );
  }

  /**
   * Reset the boundary when the block type changes — the editor can
   * swap a crashing block for a fixed one and we want the recovery
   * render to succeed rather than stay stuck on the previous error.
   */
  override componentDidUpdate(prev: Props) {
    if (prev.blockType !== this.props.blockType && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.props.blockType);
      }
      return <DefaultBlockErrorFallback blockType={this.props.blockType} />;
    }
    return this.props.children;
  }
}

function DefaultBlockErrorFallback({ blockType }: { blockType: string }) {
  const { t } = useTranslation('site-builder-runtime');
  return (
    <section
      role="alert"
      aria-label={t('block.error.regionLabel')}
      style={{
        margin: 'clamp(20px, 3vw, 32px) clamp(14px, 2.5vw, 24px)',
        padding: 'clamp(20px, 3vw, 32px)',
        border: '1px dashed var(--color-border-subtle, rgba(0, 0, 0, 0.12))',
        borderRadius: 'var(--radius-md, 8px)',
        background: 'var(--color-surface-page, rgba(127, 127, 127, 0.04))',
        textAlign: 'center',
        color: 'var(--color-text-tertiary, #6b7280)',
        position: 'relative',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{t('block.error.title')}</div>
      <div style={{ fontSize: 13, opacity: 0.7 }}>{t('block.error.description')}</div>
      <code
        style={{
          position: 'absolute',
          bottom: 8,
          right: 12,
          fontSize: 10,
          opacity: 0.4,
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        {blockType}
      </code>
    </section>
  );
}
